/**
 * Arcade run controller — milestone 2 vertical slice.
 */

import { computePurrField } from './purr.js';
import {
  type WaitingCat,
  createWaitingCat,
  refillQueue,
  removeWaitingCats,
  tickWaitingCats,
} from './queue.js';
import { type ShelfZone, canReachShelf } from './reach.js';
import {
  applyImpactRestlessness,
  tickCatRestlessness,
  tickPersonRestlessness,
} from './restlessness.js';
import { ARCADE_RUN_CONFIG, type RunConfig } from './run-config.js';
import {
  type CatGameplayState,
  applyCatnipAt,
  applyStroke,
  applyTreatCalm,
  createCatGameplayState,
  drainLeavingCats,
  endComfortHold,
  tickCoaxTimer,
  tickComfortHold,
} from './soothing.js';
import {
  type ToyEscalationState,
  type ToyStage,
  beginToyGlint,
  cancelToyGlint,
  catsInFloorToyVisibility,
  createToyEscalationState,
  exposeFloorToy,
  exposeToy,
  hideToy,
  refreshToyAffected,
  tickToyDefuse,
  tickToyEscalation,
} from './toy-escalation.js';
import type { CatArchetype, CatState, ImpactEvent } from './types.js';

export type { WaitingCat } from './queue.js';

export type RunPhase = 'playing' | 'wake_telegraph' | 'ended';

export type WakeReason = 'hungry' | 'bathroom' | 'phone';

export interface RunControllerState {
  phase: RunPhase;
  runTimeSeconds: number;
  personRestlessness: number;
  purrField: number;
  treatsRemaining: number;
  catnipRemaining: number;
  wakeReason: WakeReason | null;
  telegraphRemainingSeconds: number;
  shelfReachable: boolean;
  toyStage: ToyStage;
  toyOnShelf: boolean;
}

export class RunController {
  private config: RunConfig;
  private phase: RunPhase = 'playing';
  private runTimeSeconds = 0;
  private personRestlessness = 0;
  private treatsRemaining: number;
  private catnipRemaining: number;
  private wakeReason: WakeReason | null = null;
  private telegraphRemainingSeconds = 0;
  private readonly catPlay = new Map<string, CatGameplayState>();
  private readonly archetypes = new Map<string, CatArchetype>();
  private activeHoldCatId: string | null = null;
  private shelfReachable = false;
  private readonly toy: ToyEscalationState = createToyEscalationState();
  private archetypeList: readonly CatArchetype[] = [];
  private readonly waiting: WaitingCat[] = [];
  private heldPickup: { queueId: string; archetypeId: string; slotIndex: number } | null = null;
  private lastSpawnedId: string | null = null;

  constructor(config: RunConfig = ARCADE_RUN_CONFIG) {
    this.config = config;
    this.treatsRemaining = config.startingTreats;
    this.catnipRemaining = config.startingCatnip;
  }

  registerArchetypes(archetypes: readonly CatArchetype[]): void {
    this.archetypeList = archetypes;
    for (const a of archetypes) {
      this.archetypes.set(a.id, a);
    }
    this.waiting.length = 0;
    this.lastSpawnedId = refillQueue(this.waiting, this.archetypeList, this.config, 0, 0, null);
  }

  getWaitingCats(): readonly WaitingCat[] {
    return this.waiting;
  }

  getHeldPickup(): { archetypeId: string; x: number; y: number } | null {
    if (!this.heldPickup) return null;
    return { archetypeId: this.heldPickup.archetypeId, x: 0, y: 0 };
  }

  hasHeldPickup(): boolean {
    return this.heldPickup !== null;
  }

  heldArchetypeId(): string | null {
    return this.heldPickup?.archetypeId ?? null;
  }

  /** Pick up a waiting cat to drag onto the stack. */
  pickupWaitingCat(queueId: string): string | null {
    if (this.phase !== 'playing' || this.heldPickup) return null;
    const idx = this.waiting.findIndex((c) => c.queueId === queueId);
    if (idx < 0) return null;
    const cat = this.waiting.splice(idx, 1)[0];
    if (!cat) return null;
    cat.behavior = null;
    cat.patience = 0;
    this.heldPickup = {
      queueId: cat.queueId,
      archetypeId: cat.archetypeId,
      slotIndex: cat.slotIndex,
    };
    return cat.archetypeId;
  }

  /** Cancel drag — cat returns to its slot. */
  cancelHeldPickup(): void {
    if (!this.heldPickup) return;
    this.waiting.push(createWaitingCat(this.heldPickup.archetypeId, this.heldPickup.slotIndex));
    this.heldPickup = null;
  }

  /** Cat was placed on the stack from the queue. */
  confirmHeldPlacement(stackHeightAboveBed: number, placedCount: number): string | null {
    if (!this.heldPickup) return null;
    const archetypeId = this.heldPickup.archetypeId;
    this.lastSpawnedId = archetypeId;
    this.heldPickup = null;
    this.lastSpawnedId = refillQueue(
      this.waiting,
      this.archetypeList,
      this.config,
      stackHeightAboveBed,
      placedCount,
      this.lastSpawnedId,
    );
    return archetypeId;
  }

  getState(): RunControllerState {
    return {
      phase: this.phase,
      runTimeSeconds: this.runTimeSeconds,
      personRestlessness: this.personRestlessness,
      purrField: computePurrField(this.catPlay),
      treatsRemaining: this.treatsRemaining,
      catnipRemaining: this.catnipRemaining,
      wakeReason: this.wakeReason,
      telegraphRemainingSeconds: this.telegraphRemainingSeconds,
      shelfReachable: this.shelfReachable,
      toyStage: this.toy.stage,
      toyOnShelf: this.toy.toyOnShelf,
    };
  }

  getToyStage(): ToyStage {
    return this.toy.stage;
  }

  isToyExposed(): boolean {
    return !this.toy.toyOnShelf && this.toy.stage !== 'idle';
  }

  getCatGameplay(): ReadonlyMap<string, CatGameplayState> {
    return this.catPlay;
  }

  getCatRestlessness(): ReadonlyMap<string, CatGameplayState> {
    return this.catPlay;
  }

  getMaxCatRestlessness(): number {
    let max = 0;
    for (const entry of this.catPlay.values()) {
      if (entry.leaving) continue;
      if (entry.restlessness > max) max = entry.restlessness;
    }
    return max;
  }

  isPlaying(): boolean {
    return this.phase === 'playing';
  }

  /** Update whether the top cat can paw the shelf this frame. */
  updateShelfReach(
    catStates: readonly CatState[],
    shelf: ShelfZone,
    bedSurfaceY: number,
    stackHeightAboveBed: number,
    placedCount: number,
  ): void {
    if (this.phase !== 'playing') {
      this.shelfReachable = false;
      return;
    }
    this.shelfReachable = canReachShelf(catStates, this.archetypes, shelf, bedSurfaceY);
    if (this.toy.stage === 'curious' || this.toy.stage === 'shifting') {
      refreshToyAffected(this.toy, catStates, this.config);
    }
    if (this.waiting.length < this.config.queueVisibleCount) {
      this.lastSpawnedId = refillQueue(
        this.waiting,
        this.archetypeList,
        this.config,
        stackHeightAboveBed,
        placedCount,
        this.lastSpawnedId,
      );
    }
  }

  pawHoverShelf(hovering: boolean): void {
    if (this.phase !== 'playing' || !this.shelfReachable) return;
    if (hovering) beginToyGlint(this.toy);
    else cancelToyGlint(this.toy);
  }

  /** Grab treat (+1) or expose toy from the shelf. */
  grabShelfLoot(
    kind: 'treat' | 'toy',
    catStates: readonly CatState[],
  ): 'treat' | 'toy' | 'blocked' | null {
    if (this.phase !== 'playing' || !this.shelfReachable) return null;
    cancelToyGlint(this.toy);
    if (kind === 'treat') {
      if (this.treatsRemaining >= this.config.maxTreats) return 'blocked';
      this.treatsRemaining += 1;
      return 'treat';
    }
    if (!this.toy.toyOnShelf) return null;
    exposeToy(this.toy, catStates, this.config);
    return 'toy';
  }

  hideToyOnShelf(): void {
    if (this.phase !== 'playing') return;
    hideToy(this.toy);
  }

  tick(
    dtSeconds: number,
    catStates: readonly CatState[],
    impacts: readonly ImpactEvent[],
    stackHeightAboveBed: number,
    placedCount: number,
  ): { removedCatIds: string[]; toyCollapse: boolean; floorToyStarted: boolean } {
    if (this.phase === 'ended') {
      return { removedCatIds: [], toyCollapse: false, floorToyStarted: false };
    }

    if (this.phase === 'wake_telegraph') {
      this.telegraphRemainingSeconds -= dtSeconds;
      if (this.telegraphRemainingSeconds <= 0) {
        this.phase = 'ended';
      }
      return { removedCatIds: [], toyCollapse: false, floorToyStarted: false };
    }

    this.runTimeSeconds += dtSeconds;

    const heldId = this.heldPickup?.queueId ?? null;
    const queueResult = tickWaitingCats(
      this.waiting,
      this.archetypes,
      this.config,
      dtSeconds,
      heldId,
    );
    this.personRestlessness = clamp01(
      this.personRestlessness + queueResult.personRestlessnessRate * dtSeconds,
    );
    if (queueResult.yowlStackRestlessnessRate > 0) {
      const bottom = catsInFloorToyVisibility(catStates, this.config.toyVisibilityCatCount);
      for (const state of bottom) {
        const entry = this.catPlay.get(state.catId);
        if (!entry || entry.leaving) continue;
        entry.restlessness = clamp01(
          entry.restlessness + queueResult.yowlStackRestlessnessRate * dtSeconds,
        );
      }
    }
    let floorToyStarted = false;
    if (queueResult.triggerFloorToy && catStates.length > 0) {
      exposeFloorToy(this.toy, catStates, this.config);
      floorToyStarted = true;
    }
    if (queueResult.removedQueueIds.length > 0) {
      removeWaitingCats(this.waiting, queueResult.removedQueueIds);
      this.lastSpawnedId = refillQueue(
        this.waiting,
        this.archetypeList,
        this.config,
        stackHeightAboveBed,
        placedCount,
        this.lastSpawnedId,
      );
    }

    tickCatRestlessness(catStates, this.archetypes, this.catPlay, this.config, dtSeconds);
    applyImpactRestlessness(impacts, this.catPlay, this.config);

    const toyResult = tickToyEscalation(this.toy, this.catPlay, catStates, this.config, dtSeconds);
    if (this.toy.stage === 'shifting') {
      this.personRestlessness = clamp01(
        this.personRestlessness + this.config.toyShiftingPersonRateBonus * dtSeconds,
      );
    }
    if (toyResult.personCollapseSpike > 0) {
      this.personRestlessness = clamp01(this.personRestlessness + toyResult.personCollapseSpike);
    }
    if (this.toy.stage === 'idle' && this.toy.toyOnShelf) {
      tickToyDefuse(this.catPlay, dtSeconds, this.config.toyDefuseRestlessnessPerSecond);
    }

    if (this.activeHoldCatId) {
      const archetype = this.archetypeForCat(this.activeHoldCatId, catStates);
      const entry = this.catPlay.get(this.activeHoldCatId);
      if (archetype && entry && !entry.leaving) {
        tickComfortHold(entry, archetype, this.config, dtSeconds);
      } else {
        this.endActiveHold();
      }
    }

    for (const state of catStates) {
      const entry = this.catPlay.get(state.catId);
      if (!entry || entry.leaving) continue;
      if (!entry.holdActive) tickCoaxTimer(entry, this.config, dtSeconds);
    }

    const purrField = computePurrField(this.catPlay);
    this.personRestlessness = tickPersonRestlessness(
      this.personRestlessness,
      purrField,
      dtSeconds,
      this.config,
    );

    if (this.personRestlessness >= this.config.personWakeThreshold) {
      this.beginWake();
    }

    const removed = drainLeavingCats(this.catPlay);
    const collapse = toyResult.collapseCatIds.filter((id) => !removed.includes(id));
    return {
      removedCatIds: [...removed, ...collapse],
      toyCollapse: collapse.length > 0,
      floorToyStarted,
    };
  }

  useTreat(targetCatId: string | null, catStates: readonly CatState[]): string | null {
    if (this.phase !== 'playing' || this.treatsRemaining <= 0) return null;
    const applied = applyTreatCalm(
      this.catPlay,
      this.archetypes,
      catStates,
      targetCatId,
      this.config.treatCalmAmount,
      this.config.blissPerCalmUnit,
    );
    if (!applied) return null;
    this.treatsRemaining -= 1;
    const entry = this.catPlay.get(applied);
    if (entry) {
      entry.toyCurious = false;
      entry.toyDistracted = false;
      entry.toyCuriousTimer = 0;
    }
    return applied;
  }

  useCatnip(wx: number, wy: number, catStates: readonly CatState[]): number {
    if (this.phase !== 'playing' || this.catnipRemaining <= 0) return 0;
    const count = applyCatnipAt(wx, wy, catStates, this.archetypes, this.catPlay, this.config);
    if (count <= 0) return 0;
    this.catnipRemaining -= 1;
    return count;
  }

  registerStroke(catId: string, catStates: readonly CatState[]): boolean {
    if (this.phase !== 'playing') return false;
    const entry = this.catPlay.get(catId);
    const archetype = this.archetypeForCat(catId, catStates);
    if (!entry || entry.leaving || !archetype) return false;
    if (entry.toyDistracted && (this.toy.stage === 'curious' || this.toy.stage === 'shifting')) {
      return false;
    }
    applyStroke(entry, archetype, this.config);
    return true;
  }

  beginComfortHold(catId: string): void {
    if (this.phase !== 'playing') return;
    const entry = this.ensureCatEntry(catId);
    if (entry.leaving) return;
    if (entry.toyDistracted && (this.toy.stage === 'curious' || this.toy.stage === 'shifting')) {
      return;
    }
    this.activeHoldCatId = catId;
    entry.holdActive = true;
  }

  endActiveHold(): void {
    if (!this.activeHoldCatId) return;
    const entry = this.catPlay.get(this.activeHoldCatId);
    if (entry) endComfortHold(entry);
    this.activeHoldCatId = null;
  }

  private ensureCatEntry(catId: string): CatGameplayState {
    let entry = this.catPlay.get(catId);
    if (!entry) {
      entry = createCatGameplayState();
      this.catPlay.set(catId, entry);
    }
    return entry;
  }

  private archetypeForCat(catId: string, catStates: readonly CatState[]): CatArchetype | undefined {
    const state = catStates.find((s) => s.catId === catId);
    if (!state) return undefined;
    return this.archetypes.get(state.archetypeId);
  }

  private beginWake(): void {
    if (this.phase !== 'playing') return;
    this.endActiveHold();
    hideToy(this.toy);
    this.phase = 'wake_telegraph';
    this.telegraphRemainingSeconds = this.config.wakeTelegraphSeconds;
    const reasons: WakeReason[] = ['hungry', 'bathroom', 'phone'];
    this.wakeReason = reasons[Math.floor(Math.random() * reasons.length)] ?? 'hungry';
  }
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}
